import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, Loader2, LayoutDashboard, FileSearch, CalendarCheck, Mail, Download,
    RefreshCw, ChevronDown, ChevronUp, Sparkles, Copy, FileText, Plus, X, Users,
    IndianRupee, Trash2, ClipboardList, ShieldAlert, FolderOpen, Search, History,
    RotateCw, Ticket, Newspaper, ListTodo, MessageCircle, Pencil, AlertTriangle,
} from "lucide-react";
import { adminGet, adminPost, adminPostBody, adminPatch, adminDelete, API, UPLOAD_CATEGORY_LABELS } from "@/lib/api";
import DiagnosticFlow from "@/components/DiagnosticFlow";
import { WorkshopsAdminModule, ConsultationSettingsModule } from "@/pages/adminWorkshops";
import { DownloadsAdminModule } from "@/pages/adminDownloads";
import { ShopAdminModule } from "@/pages/adminShop";
import { Overview, LeadsModule, PaymentsModule, TrashModule, AuditModule, FilesModule, CouponsModule, BlogsModule, TasksModule, NotificationsBell, WhatsAppModule } from "@/pages/adminModules";

const MENU = [
    { id: "overview", label: "Dashboard", icon: LayoutDashboard },
    { id: "reports", label: "Reports", icon: FileSearch },
    { id: "leads", label: "Leads CRM", icon: Users },
    { id: "bookings", label: "Bookings", icon: CalendarCheck },
    { id: "consult-settings", label: "Consultation Settings", icon: CalendarCheck },
    { id: "workshops", label: "Workshops", icon: ClipboardList },
    { id: "downloads", label: "Downloads", icon: Download },
    { id: "shop", label: "Shop", icon: IndianRupee },
    { id: "payments", label: "Payments", icon: IndianRupee },
    { id: "coupons", label: "Coupons", icon: Ticket },
    { id: "submissions", label: "Submissions", icon: ClipboardList },
    { id: "files", label: "Files", icon: FolderOpen },
    { id: "blogs", label: "Blogs", icon: Newspaper },
    { id: "tasks", label: "Tasks", icon: ListTodo },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { id: "contacts", label: "Messages", icon: Mail },
    { id: "trash", label: "Trash", icon: Trash2 },
    { id: "audit", label: "Audit Logs", icon: ShieldAlert },
];

const STATUS_COLORS = {
    submitted: "bg-emerald-400/15 text-emerald-300",
    confirmed: "bg-emerald-400/15 text-emerald-300",
    paid: "bg-gold-300/15 text-gold-300",
    pending_payment: "bg-white/10 text-white/50",
    failed: "bg-red-400/15 text-red-300",
};
const ANALYSIS_COLORS = {
    ready: "bg-emerald-400/15 text-emerald-300",
    processing: "bg-gold-300/15 text-gold-300",
    failed: "bg-red-400/15 text-red-300",
};
const REGEN_SECTIONS = [
    ["full", "Full report"], ["market", "Market analysis"], ["competitor", "Competitor analysis"],
    ["website", "Website analysis"], ["seo", "SEO section"], ["social", "Social media"], ["roadmap", "Roadmap"],
];

function Pill({ status }) {
    return <span className={`rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wide ${STATUS_COLORS[status] || "bg-white/10 text-white/50"}`}>{status?.replace("_", " ")}</span>;
}
function scoreColor(v) {
    if (v <= 30) return "#f87171"; if (v <= 50) return "#fbbf24"; if (v <= 70) return "#facc15"; if (v <= 85) return "#a3e635"; return "#34d399";
}
function ScoreBar({ label, value }) {
    return (
        <div data-testid={`score-${label}`}>
            <div className="flex items-center justify-between text-xs"><span className="text-white/60">{label.replace(/_/g, " ")}</span><span className="font-display" style={{ color: scoreColor(value) }}>{value}</span></div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full" style={{ width: `${value}%`, background: scoreColor(value) }} /></div>
        </div>
    );
}
function Group({ title, items }) {
    const list = (items || []).filter(Boolean);
    if (!list.length) return null;
    return <div><p className="text-[11px] uppercase tracking-wide text-white/40">{title}</p><ul className="mt-1 space-y-1">{list.map((x, i) => <li key={i} className="text-sm text-white/75">• {x}</li>)}</ul></div>;
}

function AnalysisPanel({ a, did, token }) {
    const copyWa = () => { navigator.clipboard?.writeText(a.whatsapp_message || ""); toast.success("WhatsApp message copied"); };
    const downloadPdf = async () => {
        try {
            const resp = await fetch(`${API}/admin/diagnostics/${did}/report.pdf`, { headers: { Authorization: `Bearer ${token}` } });
            if (!resp.ok) throw new Error("pdf");
            const blob = await resp.blob(); const url = URL.createObjectURL(blob);
            const el = document.createElement("a"); el.href = url; el.download = `diagnostic_${(a.business_summary?.business_name || "report").replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(el); el.click(); el.remove(); URL.revokeObjectURL(url);
        } catch { toast.error("Could not download PDF."); }
    };
    const scores = a.scores || {}; const admin = a.admin_summary || {};
    return (
        <div className="mt-5 rounded-lg border border-gold-300/20 bg-white/[0.02] p-5" data-testid={`analysis-panel-${did}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="inline-flex items-center gap-2 font-display text-lg"><Sparkles size={16} className="text-gold-300" /> Business Analysis</p>
                <button onClick={downloadPdf} data-testid={`download-pdf-${did}`} className="ghost-button inline-flex items-center gap-2 text-xs"><FileText size={13} /> Download 18-page PDF</button>
            </div>
            <div className="mt-4 grid gap-3 rounded-md bg-ink-900/60 p-4 sm:grid-cols-2">
                <div><p className="text-[11px] uppercase tracking-wide text-white/40">Overall Readiness</p><p className="font-display text-3xl" style={{ color: scoreColor(scores.overall_growth_readiness || 0) }}>{scores.overall_growth_readiness ?? "—"}<span className="text-base text-white/40">/100</span></p></div>
                <div><p className="text-[11px] uppercase tracking-wide text-white/40">Urgency · Recommended Plan</p><p className="text-sm text-white/85">{admin.urgency_level || "—"}</p><p className="text-sm text-gold-300">{a.recommended_plan?.plan || "—"}</p></div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{Object.entries(scores).map(([k, v]) => <ScoreBar key={k} label={k} value={v} />)}</div>
            {admin.best_sales_angle && <div className="mt-5 rounded-md bg-gold-300/[0.06] p-4"><p className="text-[11px] uppercase tracking-wide text-gold-300/80">Best Sales Angle</p><p className="mt-1 text-sm text-white/85">{admin.best_sales_angle}</p></div>}
            {(a.top_issues || []).length > 0 && (
                <div className="mt-5"><p className="text-[11px] uppercase tracking-wide text-white/40">Top Issues</p><div className="mt-2 space-y-2">
                    {a.top_issues.map((iss, i) => (
                        <div key={i} className="rounded-md bg-white/[0.03] p-3 text-sm">
                            <div className="flex items-start justify-between gap-3"><p className="text-white/85">{i + 1}. {iss.problem}</p><span className="flex-none rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase text-white/60">{iss.impact}</span></div>
                            <p className="mt-1 text-white/50">{iss.suggested_fix}</p>
                        </div>
                    ))}
                </div></div>
            )}
            {a.recommended_plan?.reason && <div className="mt-5 grid gap-4 sm:grid-cols-2"><Group title="Priority — 7 days" items={a.priority_fixes?.fix_7_days} /><Group title="Priority — 30 days" items={a.priority_fixes?.fix_30_days} /></div>}
            {a.whatsapp_message && (
                <div className="mt-5 rounded-md bg-emerald-400/[0.06] p-4">
                    <div className="flex items-center justify-between"><p className="text-[11px] uppercase tracking-wide text-emerald-300/80">WhatsApp Follow-up</p><button onClick={copyWa} data-testid={`copy-wa-${did}`} className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white"><Copy size={12} /> Copy</button></div>
                    <p className="mt-1 whitespace-pre-line text-sm text-white/85">{a.whatsapp_message}</p>
                </div>
            )}
        </div>
    );
}

function VersionsModal({ did, token, onClose }) {
    const [versions, setVersions] = useState(null);
    useEffect(() => { (async () => { try { setVersions(await adminGet(`/admin/diagnostics/${did}/versions`, token)); } catch { setVersions([]); } })(); }, [did, token]);
    const restore = async (v) => { try { await adminPost(`/admin/diagnostics/${did}/restore-version/${v}`, token); toast.success(`Restored version ${v}`); onClose(true); } catch { toast.error("Could not restore"); } };
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="versions-modal">
            <div className="glass glow-border my-12 w-full max-w-md rounded-lg p-6">
                <div className="mb-4 flex items-center justify-between"><p className="font-display text-lg">Report Versions</p><button onClick={() => onClose(false)} className="text-white/50"><X size={18} /></button></div>
                {!versions ? <Loader2 className="mx-auto animate-spin text-gold-300" /> : versions.length === 0 ? <p className="py-8 text-center text-sm text-white/40">No previous versions yet. A version is saved each time you regenerate.</p> : (
                    <div className="space-y-2">
                        {versions.map((v) => (
                            <div key={v.version} className="flex items-center justify-between rounded-md glass px-4 py-3 text-sm">
                                <div><p className="text-white/85">Version {v.version}</p><p className="text-xs text-white/40">{v.label} · {new Date(v.saved_at).toLocaleString("en-IN")}</p></div>
                                <button onClick={() => restore(v.version)} className="ghost-button inline-flex items-center gap-1 text-xs"><RotateCw size={12} /> Restore</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function DiagnosticCard({ d, token, onReanalyze, onRegenerate, onDelete, onEdit, downloadFile }) {
    const [open, setOpen] = useState(false);
    const [analysis, setAnalysis] = useState(d.analysis || null);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const toggleAnalysis = async () => {
        const next = !open;
        setOpen(next);
        if (next && !analysis) {
            setLoadingAnalysis(true);
            try {
                const full = await adminGet(`/admin/diagnostics/${d.id}`, token);
                setAnalysis(full.analysis);
            } catch {
                toast.error("Could not load analysis");
            } finally {
                setLoadingAnalysis(false);
            }
        }
    };
    const [regenOpen, setRegenOpen] = useState(false);
    const [showVersions, setShowVersions] = useState(false);
    const status = d.analysis_status;
    return (
        <div className="glass glow-border rounded-lg p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div><p className="font-display text-lg">{d.details?.business_name || d.full_name}</p><p className="text-sm text-white/55">{d.full_name} · {d.email} · {d.mobile}</p></div>
                <div className="flex items-center gap-2">
                    <span className="gold-text font-display">₹{d.amount}</span>
                    <Pill status={d.status} />
                    {status && <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wide ${ANALYSIS_COLORS[status] || "bg-white/10 text-white/50"}`}>{status === "processing" && <Loader2 size={11} className="animate-spin" />}{status === "ready" ? "Ready" : status === "processing" ? "Analyzing" : "Failed"}</span>}
                </div>
            </div>

            {d.details && (
                <div className="mt-5 grid gap-2 text-sm sm:grid-cols-2">
                    {[
                        ["Category", d.details.business_category], ["Website", d.details.website || d.details.business_link],
                        ["Google Map", d.details.google_map_link], ["Instagram", d.details.instagram_link], ["LinkedIn", d.details.linkedin_link],
                        ["Facebook", d.details.facebook_link], ["Address", d.details.full_address], ["GST", d.details.gst_number],
                        ["Employees", d.details.employee_count], ["Business type", d.details.business_type],
                        ["Competitor 1", [d.details.competitor_1, d.details.competitor_1_website].filter(Boolean).join(" · ")],
                        ["Competitor 2", [d.details.competitor_2, d.details.competitor_2_website].filter(Boolean).join(" · ")],
                        ["Competitor 3", [d.details.competitor_3, d.details.competitor_3_website].filter(Boolean).join(" · ")],
                        ["Revenue", d.details.monthly_revenue], ["Growth target", d.details.growth_target], ["Main Problem", d.details.main_problem],
                        ["Support needed", d.details.support_needed], ["Website age", d.details.website_age], ["Margin %", d.details.margin_percentage],
                        ["Niche / audience", d.details.niche_audience], ["Social media mgmt", d.details.social_media_mgmt],
                        ["Digital marketing", d.details.digital_marketing], ["Worked w/ agency before", d.details.worked_with_agency_before],
                        ["Currently w/ agency", d.details.currently_with_agency], ["Mobile app", d.details.has_mobile_app],
                        ["Selling online", d.details.selling_online], ["Attended events", d.details.attended_events],
                        ["Awards", d.details.received_awards], ["PR / media", d.details.pr_activity],
                    ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex gap-3 rounded-md bg-white/[0.03] px-4 py-2.5"><span className="w-32 flex-none text-white/40">{k}</span><span className="text-white/80 break-words">{v}</span></div>
                    ))}
                </div>
            )}

            {d.files && d.files.length > 0 && (
                <div className="mt-5"><p className="label-eyebrow mb-3">Uploaded files ({d.files.length})</p><div className="space-y-3">
                    {Object.entries(d.files.reduce((acc, f) => { const c = f.category || "other"; (acc[c] = acc[c] || []).push(f); return acc; }, {})).map(([cat, fs]) => (
                        <div key={cat}><p className="text-[11px] uppercase tracking-wide text-white/40">{UPLOAD_CATEGORY_LABELS[cat] || cat}</p><div className="mt-1.5 flex flex-wrap gap-2">
                            {fs.map((f) => <button key={f.id} onClick={() => downloadFile(f.id, f.original_filename)} data-testid={`admin-file-${f.id}`} className="inline-flex items-center gap-2 rounded-md glass glass-hover px-3 py-2 text-xs text-white/75"><Download size={13} className="text-gold-300" /> {f.original_filename}</button>)}
                        </div></div>
                    ))}
                </div></div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
                {status === "ready" && <button onClick={toggleAnalysis} data-testid={`toggle-analysis-${d.id}`} className="gold-button inline-flex items-center gap-2 text-sm">{loadingAnalysis ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {open ? "Hide" : "View"} Analysis {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>}
                {status === "processing" && <span className="inline-flex items-center gap-2 text-sm text-white/55"><Loader2 size={14} className="animate-spin text-gold-300" /> Generating…</span>}
                {(status === "failed" || !status) && d.status === "submitted" && <button onClick={() => onReanalyze(d.id)} data-testid={`run-analysis-${d.id}`} className="gold-button inline-flex items-center gap-2 text-sm"><Sparkles size={14} /> {status === "failed" ? "Retry Analysis" : "Run Analysis"}</button>}

                {status === "ready" && (
                    <div className="relative">
                        <button onClick={() => setRegenOpen((o) => !o)} data-testid={`regen-menu-${d.id}`} className="ghost-button inline-flex items-center gap-2 text-sm"><RefreshCw size={13} /> Regenerate <ChevronDown size={13} /></button>
                        {regenOpen && (
                            <div className="absolute z-20 mt-2 w-48 rounded-lg border border-white/10 bg-ink-900 p-1 shadow-xl">
                                {REGEN_SECTIONS.map(([key, label]) => (
                                    <button key={key} onClick={() => { setRegenOpen(false); onRegenerate(d.id, key); }} data-testid={`regen-${key}-${d.id}`} className="block w-full rounded-md px-3 py-2 text-left text-xs text-white/75 hover:bg-white/5">{label}</button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {(d.versions_count > 0 || d.versions?.length > 0 || status === "ready") && <button onClick={() => setShowVersions(true)} data-testid={`versions-${d.id}`} className="ghost-button inline-flex items-center gap-2 text-sm"><History size={13} /> Versions</button>}
                <button onClick={() => onEdit(d)} data-testid={`edit-report-${d.id}`} className="ml-auto inline-flex items-center gap-2 rounded-md glass glass-hover px-3 py-2 text-xs text-white/75"><Pencil size={13} className="text-gold-300" /> Edit</button>
                <button onClick={() => onDelete(d.id)} data-testid={`delete-report-${d.id}`} className="inline-flex items-center gap-2 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300 hover:bg-red-500/20"><Trash2 size={13} /> Delete</button>
            </div>

            {open && status === "ready" && (loadingAnalysis ? <div className="mt-5 flex justify-center py-8"><Loader2 className="animate-spin text-gold-300" size={22} /></div> : analysis && <AnalysisPanel a={analysis} did={d.id} token={token} />)}
            <p className="mt-4 text-xs text-white/35">{new Date(d.created_at).toLocaleString("en-IN")}</p>
            {showVersions && <VersionsModal did={d.id} token={token} onClose={() => setShowVersions(false)} />}
        </div>
    );
}

export default function AdminDashboard() {

function EditModal({ title, fields, initial, onSave, onClose }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try { await onSave(form); } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="edit-modal">
            <div className="my-12 w-full max-w-lg glass glow-border rounded-xl p-6">
                <div className="mb-5 flex items-center justify-between"><p className="font-display text-lg">{title}</p><button onClick={onClose} className="text-white/50"><X size={18} /></button></div>
                <div className="grid gap-4 sm:grid-cols-2">
                    {fields.map((f) => (
                        <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                            <label className="text-[11px] uppercase tracking-wide text-white/40">{f.label}</label>
                            {f.options ? (
                                <select value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`edit-${f.key}`} className="mt-1 block w-full rounded-md bg-ink-900 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-gold-300/40">
                                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            ) : (
                                <input value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? e.target.value : e.target.value })} type={f.type || "text"} data-testid={`edit-${f.key}`} className="mt-1 block w-full rounded-md bg-white/5 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-gold-300/40" />
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="ghost-button text-sm">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="edit-save" className="gold-button inline-flex items-center gap-2 text-sm disabled:opacity-50">{saving && <Loader2 size={14} className="animate-spin" />} Save changes</button>
                </div>
            </div>
        </div>
    );
}

function ClearDataModal({ token, onClose, onDone }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try { const r = await adminPostBody("/admin/danger/clear-data", { confirm: "DELETE", scope: "all" }, token); toast.success("Dashboard data cleared"); onDone?.(r); onClose(); }
        catch { toast.error("Could not clear data"); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="clear-data-modal">
            <div className="my-16 w-full max-w-md glass glow-border rounded-xl p-6">
                <div className="mb-3 flex items-center gap-3 text-red-300"><AlertTriangle size={20} /><p className="font-display text-lg">Clear all dashboard data</p></div>
                <p className="text-sm text-white/60">This permanently deletes all bookings, reports/submissions, payments, leads, contacts, notifications and logs. Coupons and blog posts are kept. This cannot be undone.</p>
                <label className="mt-5 block text-[11px] uppercase tracking-wide text-white/40">Type DELETE to confirm</label>
                <input value={text} onChange={(e) => setText(e.target.value)} data-testid="clear-confirm-input" placeholder="DELETE" className="mt-1 block w-full rounded-md bg-white/5 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-red-400/40" />
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="ghost-button text-sm">Cancel</button>
                    <button onClick={run} disabled={text !== "DELETE" || busy} data-testid="clear-confirm-btn" className="inline-flex items-center gap-2 rounded-md bg-red-500/90 px-4 py-2 text-sm font-medium text-white disabled:opacity-40">{busy && <Loader2 size={14} className="animate-spin" />} Delete everything</button>
                </div>
            </div>
        </div>
    );
}

    const nav = useNavigate();
    const [view, setView] = useState("overview");
    const [reports, setReports] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [editBooking, setEditBooking] = useState(null);
    const [editReport, setEditReport] = useState(null);
    const [showClear, setShowClear] = useState(false);
    const [search, setSearch] = useState("");
    const [searchRes, setSearchRes] = useState(null);
    const pollRef = useRef(null);
    const token = localStorage.getItem("admin_token");
    const user = JSON.parse(localStorage.getItem("admin_user") || "null");

    const logout = useCallback(() => { localStorage.removeItem("admin_token"); localStorage.removeItem("admin_user"); nav("/admin", { replace: true }); }, [nav]);

    const loadReports = useCallback(async (silent) => {
        if (!silent) setLoading(true);
        try { setReports(await adminGet("/admin/diagnostics", token)); }
        catch (err) { if (err?.response?.status === 401) { logout(); } else if (!silent) toast.error("Could not load reports."); }
        finally { if (!silent) setLoading(false); }
    }, [token, logout]);

    const loadSimple = useCallback(async (path, setter) => {
        setLoading(true);
        try { setter(await adminGet(path, token)); } catch { toast.error("Could not load data."); } finally { setLoading(false); }
    }, [token]);

    useEffect(() => {
        if (!token) { nav("/admin", { replace: true }); return; }
        if (view === "reports" || view === "submissions") loadReports();
        else if (view === "bookings") loadSimple("/admin/bookings", setBookings);
        else if (view === "contacts") loadSimple("/admin/contacts", setContacts);
    }, [view, token, nav, loadReports, loadSimple]);

    useEffect(() => {
        if (pollRef.current) clearTimeout(pollRef.current);
        if ((view === "reports" || view === "submissions") && reports.some((d) => d.analysis_status === "processing")) {
            pollRef.current = setTimeout(() => loadReports(true), 7000);
        }
        return () => pollRef.current && clearTimeout(pollRef.current);
    }, [reports, view, loadReports]);

    const reanalyze = async (id) => {
        try { await adminPost(`/admin/diagnostics/${id}/reanalyze`, token); toast.success("Analysis started…"); setReports((p) => p.map((d) => d.id === id ? { ...d, analysis_status: "processing" } : d)); }
        catch { toast.error("Could not start analysis."); }
    };
    const regenerate = async (id, section) => {
        try { await adminPostBody(`/admin/diagnostics/${id}/regenerate`, { section }, token); toast.success(`Regenerating (${section})… a version was saved.`); setReports((p) => p.map((d) => d.id === id ? { ...d, analysis_status: "processing" } : d)); }
        catch { toast.error("Could not regenerate."); }
    };
    const deleteReport = async (id) => {
        if (!window.confirm("Move this report to Trash?")) return;
        try { await adminDelete(`/admin/diagnostics/${id}`, token); toast.success("Moved to Trash"); setReports((p) => p.filter((d) => d.id !== id)); }
        catch { toast.error("Could not delete."); }
    };
    const saveReportEdit = async (form) => {
        try { await adminPatch(`/admin/diagnostics/${form.id}/edit`, form, token); toast.success("Report updated"); setEditReport(null); loadReports(true); }
        catch { toast.error("Could not update report."); }
    };
    const deleteBooking = async (id) => {
        if (!window.confirm("Permanently delete this booking?")) return;
        try { await adminDelete(`/admin/bookings/${id}`, token); toast.success("Booking deleted"); setBookings((p) => p.filter((b) => b.id !== id)); }
        catch { toast.error("Could not delete booking."); }
    };
    const saveBookingEdit = async (form) => {
        try { await adminPatch(`/admin/bookings/${form.id}`, { ...form, amount: form.amount === "" ? null : Number(form.amount) }, token); toast.success("Booking updated"); setEditBooking(null); loadSimple("/admin/bookings", setBookings); }
        catch { toast.error("Could not update booking."); }
    };
    const downloadFile = async (fileId, filename) => {
        try {
            const resp = await fetch(`${API}/admin/files/${fileId}/download`, { headers: { Authorization: `Bearer ${token}` } });
            if (!resp.ok) throw new Error("download failed");
            const blob = await resp.blob(); const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = filename || "file"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch { toast.error("Could not download file."); }
    };

    const runSearch = async (q) => {
        setSearch(q);
        if (q.length < 2) { setSearchRes(null); return; }
        try { setSearchRes(await adminGet(`/admin/search?q=${encodeURIComponent(q)}`, token)); } catch { /* ignore */ }
    };

    const curMenu = MENU.find((m) => m.id === view);

    return (
        <div data-testid="admin-dashboard" className="flex min-h-screen">
            {/* Sidebar */}
            <aside className="sticky top-0 hidden h-screen w-60 flex-none flex-col border-r border-white/8 bg-ink-900/95 px-4 py-6 lg:flex">
                <p className="px-3 font-display text-xl font-semibold">Abhijit <span className="gold-text">JK</span></p>
                <p className="px-3 text-[11px] uppercase tracking-wide text-white/35">Control Center</p>
                <nav className="mt-8 flex-1 space-y-1">
                    {MENU.map((m) => {
                        const Icon = m.icon; const active = view === m.id;
                        return (
                            <button key={m.id} onClick={() => setView(m.id)} data-testid={`nav-${m.id}`}
                                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition ${active ? "bg-gold-300 text-black" : "text-white/65 hover:bg-white/5 hover:text-white"}`}>
                                <Icon size={16} /> {m.label}
                            </button>
                        );
                    })}
                </nav>
                <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/55 hover:bg-white/5 hover:text-white"><LogOut size={16} /> Logout</button>
            </aside>

            <div className="flex-1">
                {/* Topbar */}
                <header className="sticky top-0 z-30 border-b border-white/5 bg-ink-900/80 backdrop-blur-xl">
                    <div className="flex items-center justify-between gap-4 px-5 py-4 lg:px-8">
                        <div className="flex items-center gap-3">
                            <select value={view} onChange={(e) => setView(e.target.value)} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white lg:hidden">
                                {MENU.map((m) => <option key={m.id} value={m.id} className="bg-ink-900">{m.label}</option>)}
                            </select>
                            <h1 className="font-display text-xl">{curMenu?.label}</h1>
                        </div>
                        <div className="relative flex items-center gap-3">
                            <div className="relative hidden sm:block">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
                                <input value={search} onChange={(e) => runSearch(e.target.value)} placeholder="Search clients, reports…" data-testid="global-search"
                                    className="w-56 rounded-full border border-white/10 bg-white/5 py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/30" />
                                {searchRes && (
                                    <div className="absolute right-0 z-40 mt-2 max-h-80 w-80 overflow-y-auto rounded-lg border border-white/10 bg-ink-900 p-2 shadow-2xl" data-testid="search-results">
                                        {["reports", "leads", "bookings"].map((k) => (searchRes[k] || []).length > 0 && (
                                            <div key={k} className="mb-2">
                                                <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-white/35">{k}</p>
                                                {searchRes[k].map((r) => (
                                                    <button key={r.id} onClick={() => { setView(k === "reports" ? "reports" : k); setSearchRes(null); setSearch(""); }} className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-white/75 hover:bg-white/5">
                                                        {r.full_name || r.name} <span className="text-xs text-white/40">{r.email}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                        {!["reports", "leads", "bookings"].some((k) => (searchRes[k] || []).length) && <p className="px-2 py-3 text-center text-sm text-white/40">No matches.</p>}
                                    </div>
                                )}
                            </div>
                            {(view === "reports" || view === "submissions") && <button onClick={() => setShowManual(true)} data-testid="new-manual-btn" className="gold-button inline-flex items-center gap-2 text-sm"><Plus size={15} /> New Analysis</button>}
                            {["reports", "submissions", "bookings"].includes(view) && <button onClick={() => setShowClear(true)} data-testid="clear-data-btn" className="inline-flex items-center gap-2 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300 hover:bg-red-500/20"><AlertTriangle size={14} /> Clear data</button>}
                            <NotificationsBell token={token} />
                        </div>
                    </div>
                </header>

                <main className="px-5 py-8 lg:px-8">
                    {view === "overview" && <Overview token={token} onGo={setView} />}
                    {view === "leads" && <LeadsModule token={token} />}
                    {view === "payments" && <PaymentsModule token={token} />}
                    {view === "trash" && <TrashModule token={token} />}
                    {view === "audit" && <AuditModule token={token} />}
                    {view === "files" && <FilesModule token={token} downloadFile={downloadFile} />}
                    {view === "coupons" && <CouponsModule token={token} />}
                    {view === "blogs" && <BlogsModule token={token} />}
                    {view === "tasks" && <TasksModule token={token} />}
                    {view === "whatsapp" && <WhatsAppModule token={token} />}
                    {view === "workshops" && <WorkshopsAdminModule token={token} />}
                    {view === "downloads" && <DownloadsAdminModule token={token} />}
                    {view === "shop" && <ShopAdminModule token={token} />}
                    {view === "consult-settings" && <ConsultationSettingsModule token={token} />}

                    {(view === "reports" || view === "submissions") && (
                        loading ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold-300" size={28} /></div>
                        : reports.length === 0 ? <div data-testid="admin-empty" className="glass rounded-lg py-24 text-center text-white/50">No reports yet.</div>
                        : <div className="grid gap-5" data-testid="admin-diagnostics-list">{reports.map((d) => <DiagnosticCard key={d.id} d={d} token={token} onReanalyze={reanalyze} onRegenerate={regenerate} onDelete={deleteReport} onEdit={setEditReport} downloadFile={downloadFile} />)}</div>
                    )}

                    {view === "bookings" && (
                        loading ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold-300" size={28} /></div>
                        : bookings.length === 0 ? <div className="glass rounded-lg py-24 text-center text-white/50">No bookings yet.</div>
                        : <div className="grid gap-5" data-testid="admin-bookings-list">{bookings.map((b) => (
                            <div key={b.id} className="glass glow-border rounded-lg p-6">
                                <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="font-display text-lg">{b.full_name}</p><p className="text-sm text-white/55">{b.email} · {b.mobile}</p></div><div className="flex items-center gap-3"><span className="gold-text font-display">₹{b.amount}</span><Pill status={b.status} /></div></div>
                                <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/65"><span>{b.date} · {b.slot_label}</span></div>
                                {b.details && <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">{[["Business", b.details.business_name], ["Category", b.details.business_category], ["Problem", b.details.current_problem], ["Support", b.details.support_needed], ["Expected", b.details.expected_result]].filter(([, v]) => v).map(([k, v]) => <div key={k} className="flex gap-3 rounded-md bg-white/[0.03] px-4 py-2.5"><span className="w-24 flex-none text-white/40">{k}</span><span className="text-white/80">{v}</span></div>)}</div>}
                                <div className="mt-4 flex items-center gap-3">
                                    <p className="text-xs text-white/35">{new Date(b.created_at).toLocaleString("en-IN")}</p>
                                    <button onClick={() => setEditBooking(b)} data-testid={`edit-booking-${b.id}`} className="ml-auto inline-flex items-center gap-2 rounded-md glass glass-hover px-3 py-2 text-xs text-white/75"><Pencil size={13} className="text-gold-300" /> Edit</button>
                                    <button onClick={() => deleteBooking(b.id)} data-testid={`delete-booking-${b.id}`} className="inline-flex items-center gap-2 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300 hover:bg-red-500/20"><Trash2 size={13} /> Delete</button>
                                </div>
                            </div>
                        ))}</div>
                    )}

                    {view === "contacts" && (
                        loading ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold-300" size={28} /></div>
                        : contacts.length === 0 ? <div className="glass rounded-lg py-24 text-center text-white/50">No messages yet.</div>
                        : <div className="grid gap-5" data-testid="admin-contacts-list">{contacts.map((c) => (
                            <div key={c.id} className="glass glow-border rounded-lg p-6"><div className="flex flex-wrap items-center justify-between gap-3"><p className="font-display text-lg">{c.name}</p><p className="text-sm text-white/55">{c.email} · {c.phone}</p></div><p className="mt-3 text-sm text-white/75">{c.message}</p><p className="mt-4 text-xs text-white/35">{new Date(c.created_at).toLocaleString("en-IN")}</p></div>
                        ))}</div>
                    )}
                </main>
            </div>

            {showManual && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="manual-modal">
                    <div className="my-8 w-full max-w-3xl">
                        <div className="mb-3 flex items-center justify-between"><p className="font-display text-xl">Manual Business Analysis</p><button onClick={() => { setShowManual(false); loadReports(true); }} data-testid="manual-close" className="glass glass-hover flex h-9 w-9 items-center justify-center rounded-full text-white/70"><X size={16} /></button></div>
                        <div className="glass glow-border rounded-lg p-8 md:p-10"><DiagnosticFlow adminMode token={token} /></div>
                    </div>
                </div>
            )}

            {editBooking && (
                <EditModal
                    title="Edit Booking"
                    onClose={() => setEditBooking(null)}
                    onSave={saveBookingEdit}
                    initial={{ id: editBooking.id, full_name: editBooking.full_name || "", email: editBooking.email || "", mobile: editBooking.mobile || "", date: editBooking.date || "", slot_label: editBooking.slot_label || "", amount: editBooking.amount ?? "", status: editBooking.status || "paid", meeting_link: editBooking.meeting_link || "" }}
                    fields={[
                        { key: "full_name", label: "Full name" },
                        { key: "email", label: "Email" },
                        { key: "mobile", label: "Mobile" },
                        { key: "amount", label: "Amount (₹)", type: "number" },
                        { key: "date", label: "Date (YYYY-MM-DD)" },
                        { key: "slot_label", label: "Slot label" },
                        { key: "status", label: "Status", options: ["pending_payment", "paid", "confirmed", "failed", "cancelled"] },
                        { key: "meeting_link", label: "Meeting link", full: true },
                    ]}
                />
            )}

            {editReport && (
                <EditModal
                    title="Edit Report"
                    onClose={() => setEditReport(null)}
                    onSave={saveReportEdit}
                    initial={{ id: editReport.id, full_name: editReport.full_name || "", email: editReport.email || "", mobile: editReport.mobile || "", amount: editReport.amount ?? "", business_name: editReport.details?.business_name || "", business_category: editReport.details?.business_category || "" }}
                    fields={[
                        { key: "full_name", label: "Full name" },
                        { key: "email", label: "Email" },
                        { key: "mobile", label: "Mobile" },
                        { key: "amount", label: "Amount (₹)", type: "number" },
                        { key: "business_name", label: "Business name" },
                        { key: "business_category", label: "Category" },
                    ]}
                />
            )}

            {showClear && (
                <ClearDataModal token={token} onClose={() => setShowClear(false)} onDone={() => { setReports([]); setBookings([]); setContacts([]); }} />
            )}
        </div>
    );
}
